import React, { useMemo, useCallback } from 'react';
import { Space, Popover } from 'antd';
import { HeartOutlined, HeartFilled } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import log from '@guuru/logger-web';
import { REACTIONS } from '@guuru/news-common';
import type { Sentiment } from '@guuru/news-common';
import NewsEmojiPicker from './NewsEmojiPicker';
import { HeartButton, ReactionButton } from './style';
import TOGGLE_NEWS_REACTION from './mutations/toggleNewsReaction';

interface Reaction {
  content: Sentiment;
  viewerHasReacted: boolean;
  count: number;
}

interface NewsReactionsProps {
  partnerId: string;
  newsId: string;
  reactions: Reaction[];
}

const findReaction = (reactions: Reaction[], sentiment: Sentiment) => (
  reactions.find((r) => r.content === sentiment)
);

const NewsReactions = function ({
  partnerId,
  newsId,
  reactions,
}: NewsReactionsProps) {
  const [toggleReaction] = useMutation(TOGGLE_NEWS_REACTION);

  const handleReactionClick = useCallback((sentiment: Sentiment) => {
    toggleReaction({
      variables: {
        partnerId,
        input: { newsId, content: sentiment },
      },
    }).catch((error) => {
      log.error(error);
    });
  }, [newsId, partnerId, toggleReaction]);

  const otherReactions = useMemo(() => reactions
    .filter((reaction) => reaction.content !== 'love')
    .map((reaction) => (
      <ReactionButton
        key={reaction.content}
        $isActive={reaction.viewerHasReacted}
        onClick={() => handleReactionClick(reaction.content)}
      >
        <span>{REACTIONS[reaction.content].emoji}</span>
        <span>{reaction.count}</span>
      </ReactionButton>
    )), [handleReactionClick, reactions]);

  const love = useMemo(() => findReaction(reactions, 'love'), [reactions]);
  const hasLove = !!love?.count;

  return (
    <Space size={4}>
      <Popover
        content={<NewsEmojiPicker onEmojiSelect={handleReactionClick} />}
        trigger={'ontouchend' in window ? 'click' : 'hover'}
        placement="topLeft"
        styles={{ body: { borderRadius: '50px', padding: 0, boxShadow: 'none' } }}
        arrow={false}
        mouseLeaveDelay={0.5}
      >
        <HeartButton
          $isActive={love?.viewerHasReacted}
          $hasReactions={hasLove}
          onClick={() => {
            if (!('ontouchend' in window)) {
              handleReactionClick('love');
            }
          }}
        >
          {hasLove ? <HeartFilled /> : <HeartOutlined />}
          {hasLove && <span>{love.count}</span>}
        </HeartButton>
      </Popover>
      {otherReactions}
    </Space>
  );
};

export default NewsReactions;
